import { defineStore } from 'pinia';
import { useOrganizationStore } from '@/store/organizationStore';
import { apiRequestHandler } from '@/utils/api';
import { PreparedItem, UpdateItem } from '@/models/item.models';
import { uniq } from 'lodash-es';
import {
  FindItemsData,
  FindJobsData,
  Folder,
  ItemType,
  Job,
  JobsService,
  Relationship,
  SubmitCopyItemJob,
  SubmitCopyStreamJob,
  SubmitImportRunJob,
  UpdateWorkItem,
  UpdateWorkItemExtended,
  UpdateWorkItemRelationshipsExtended,
  WorkItem,
} from '@/api';

export interface JobsStateInterface {
  allJobs: Array<Job>;
  duplicateItemDialog: {
    show: boolean;
    itemToDuplicate: PreparedItem | null;
  };
}

export const useJobsStore = defineStore('jobs', {
  /****
   STATE
   ****/
  state: (): JobsStateInterface => ({
    allJobs: [],
    duplicateItemDialog: {
      show: false,
      itemToDuplicate: null,
    },
  }),

  /****
   GETTERS
   ****/
  getters: {
    duplicateItemDialogShown(): boolean {
      return this.duplicateItemDialog.show;
    },
  },

  /****
   ACTIONS
   ****/
  actions: {
    async getJobHistory(params: FindJobsData['query']) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await JobsService.findJobs({
          path: {
            organizationId,
          },
          query: {
            ...params,
          },
        });
        return response.data;
      });
    },

    async getJobById(jobId: string) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await JobsService.getJobById({
          path: {
            organizationId,
            jobId,
          },
        });
        return response.data.data;
      });
    },

    async submitCopyStreamJob(data: SubmitCopyStreamJob) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await JobsService.submitCopyStreamJob({
          path: {
            organizationId,
          },
          body: data,
        });
        this.allJobs.push(response.data.data);
        return response.data.data;
      });
    },

    async submitCopyItemJob(data: SubmitCopyItemJob) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await JobsService.submitCopyItemJob({
          path: {
            organizationId,
          },
          body: data,
        });
        this.allJobs.push(response.data.data);
        return response.data.data;
      });
    },

    async submitUpdateItemsJob(data: {
      items: Array<Folder | WorkItem>;
      data: UpdateItem;
      addedLabelIds?: string[];
      removedLabelIds?: string[];
      addedRelationships?: Array<Relationship>;
      removedRelationships?: Array<Relationship>;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        let workItems: Array<WorkItem> = data.items.filter(
          (item) => item.itemType === ItemType.WORKITEM
        ) as Array<WorkItem>;
        const folders = data.items.filter(
          (item) => item.itemType === ItemType.FOLDER
        );

        if (data.addedLabelIds?.length) {
          workItems = workItems.map((item) => ({
            ...item,
            labels: uniq([
              ...(item as WorkItem).labels,
              ...(data.addedLabelIds as string[]),
            ]),
          }));
        }

        if (data.removedLabelIds?.length) {
          workItems = workItems.map((item) => ({
            ...item,
            labels: (item as WorkItem).labels.filter(
              (label) => !(data.removedLabelIds as string[]).includes(label)
            ),
          }));
        }

        if (data.addedRelationships?.length) {
          workItems = workItems.map((item) => ({
            ...item,
            relationships: data.addedRelationships as Array<Relationship>,
          }));
        }

        if (data.removedRelationships?.length) {
          workItems = workItems.map((item) => ({
            ...item,
            relationships: data.removedRelationships as Array<Relationship>,
          }));
        }

        const requestWorkItems = workItems.map((item) => ({
          number: item.number,
          ...data.data,
          ...(data.addedLabelIds?.length || data.removedLabelIds?.length
            ? {
                labels: (item as WorkItem).labels,
              }
            : {}),
          ...(data.addedRelationships?.length
            ? {
                addedRelationships: (item as WorkItem).relationships,
              }
            : {}),
          ...(data.removedRelationships?.length
            ? {
                removedRelationships: (item as WorkItem).relationships,
              }
            : {}),
        }));
        const requestFolders = folders.map((item) => ({
          number: item.number,
          ...data.data,
        }));

        const isRelationshipUpdate =
          data.addedRelationships?.length || data.removedRelationships?.length;
        const response = await JobsService.submitUpdateItemsJob({
          path: {
            organizationId,
          },
          body: {
            input: {
              workItems: isRelationshipUpdate
                ? []
                : (requestWorkItems as UpdateWorkItemExtended),
              folders: requestFolders,
              workItemRelationships: isRelationshipUpdate
                ? (requestWorkItems as UpdateWorkItemRelationshipsExtended)
                : [],
            },
          },
        });
        this.allJobs.push(response.data.data);
        return response.data.data;
      });
    },

    async submitUpdateFilteredItemsJob(data: {
      filter: FindItemsData['query'];
      update: UpdateWorkItem;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await JobsService.submitUpdateFilteredItemsJob({
          path: {
            organizationId,
          },
          body: {
            input: {
              filter: data.filter ?? {},
              update: data.update,
            },
          },
        });
        this.allJobs.push(response.data?.data);
        return response.data.data;
      });
    },

    async submitImportTemplateJob(
      data: SubmitImportRunJob,
      sendToJobStore = true
    ) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await JobsService.submitCreateImportRun({
          path: {
            organizationId,
          },
          body: data,
        });
        if (sendToJobStore) {
          this.allJobs.push(response.data.data);
        }
        return response.data.data;
      });
    },

    addJobToCache(job: Job) {
      if (this.allJobs.find((j) => j.id === job.id)) return;
      this.allJobs.push(job);
    },

    updateJobCache(job: Job) {
      const jobIndex = this.allJobs.findIndex((j) => j.id === job.id);
      if (jobIndex === -1) return;
      const cachedJob = this.allJobs[jobIndex];
      this.allJobs[jobIndex] = {
        ...job,
        type: cachedJob.type, // makes sure a frontend type is not overwritten
      };
    },

    showDuplicateItemDialog(item: PreparedItem) {
      this.duplicateItemDialog.show = true;
      this.duplicateItemDialog.itemToDuplicate = item;
    },

    hideDuplicateItemDialog() {
      this.duplicateItemDialog.show = false;
      this.duplicateItemDialog.itemToDuplicate = null;
    },
  },
});
